import type { Market } from './market'

type SpellingPair = {
  us: string
  gb: string
}

const SPELLING_PAIRS: SpellingPair[] = [
  // -ence / -ense
  { us: 'defense', gb: 'defence' },
  { us: 'defenses', gb: 'defences' },
  { us: 'offense', gb: 'offence' },
  { us: 'offenses', gb: 'offences' },
  { us: 'pretense', gb: 'pretence' },

  // -our / -or
  { us: 'behavior', gb: 'behaviour' },
  { us: 'behaviors', gb: 'behaviours' },
  { us: 'behavioral', gb: 'behavioural' },
  { us: 'color', gb: 'colour' },
  { us: 'colors', gb: 'colours' },
  { us: 'coloring', gb: 'colouring' },
  { us: 'favorite', gb: 'favourite' },
  { us: 'favorites', gb: 'favourites' },
  { us: 'honor', gb: 'honour' },
  { us: 'honors', gb: 'honours' },
  { us: 'honored', gb: 'honoured' },
  { us: 'honoring', gb: 'honouring' },
  { us: 'humor', gb: 'humour' },
  { us: 'neighbor', gb: 'neighbour' },
  { us: 'neighbors', gb: 'neighbours' },
  { us: 'neighborhood', gb: 'neighbourhood' },
  { us: 'favor', gb: 'favour' },
  { us: 'favors', gb: 'favours' },
  { us: 'labor', gb: 'labour' },
  { us: 'vigor', gb: 'vigour' },

  // -ise / -ize
  { us: 'organize', gb: 'organise' },
  { us: 'organizes', gb: 'organises' },
  { us: 'organized', gb: 'organised' },
  { us: 'organizing', gb: 'organising' },
  { us: 'organization', gb: 'organisation' },
  { us: 'organizations', gb: 'organisations' },
  { us: 'customize', gb: 'customise' },
  { us: 'customizes', gb: 'customises' },
  { us: 'customized', gb: 'customised' },
  { us: 'customizing', gb: 'customising' },
  { us: 'recognize', gb: 'recognise' },
  { us: 'recognizes', gb: 'recognises' },
  { us: 'recognized', gb: 'recognised' },
  { us: 'recognizing', gb: 'recognising' },
  { us: 'analyze', gb: 'analyse' },
  { us: 'analyzes', gb: 'analyses' },
  { us: 'analyzed', gb: 'analysed' },
  { us: 'analyzing', gb: 'analysing' },
  { us: 'realize', gb: 'realise' },
  { us: 'realizes', gb: 'realises' },
  { us: 'realized', gb: 'realised' },
  { us: 'realizing', gb: 'realising' },
  { us: 'apologize', gb: 'apologise' },
  { us: 'apologizes', gb: 'apologises' },
  { us: 'apologized', gb: 'apologised' },
  { us: 'minimize', gb: 'minimise' },
  { us: 'minimizes', gb: 'minimises' },
  { us: 'minimized', gb: 'minimised' },
  { us: 'specialize', gb: 'specialise' },
  { us: 'specializes', gb: 'specialises' },
  { us: 'specialized', gb: 'specialised' },
  { us: 'specializing', gb: 'specialising' },
  { us: 'utilize', gb: 'utilise' },
  { us: 'utilizes', gb: 'utilises' },
  { us: 'utilized', gb: 'utilised' },
  { us: 'utilizing', gb: 'utilising' },
  { us: 'prioritize', gb: 'prioritise' },
  { us: 'prioritizes', gb: 'prioritises' },
  { us: 'prioritized', gb: 'prioritised' },
  { us: 'prioritizing', gb: 'prioritising' },
  { us: 'authorize', gb: 'authorise' },
  { us: 'authorizes', gb: 'authorises' },
  { us: 'authorized', gb: 'authorised' },
  { us: 'unauthorized', gb: 'unauthorised' },
  { us: 'authorization', gb: 'authorisation' },

  // -ce / -se (nouns)
  { us: 'license', gb: 'licence' },
  { us: 'licenses', gb: 'licences' },
  { us: 'licensed', gb: 'licenced' },
  { us: 'licensing', gb: 'licencing' },

  // -re / -er
  { us: 'center', gb: 'centre' },
  { us: 'centers', gb: 'centres' },
  { us: 'centered', gb: 'centred' },
  { us: 'centering', gb: 'centring' },

  // -l / -ll (verb inflections)
  { us: 'canceled', gb: 'cancelled' },
  { us: 'canceling', gb: 'cancelling' },
  { us: 'labeled', gb: 'labelled' },
  { us: 'labeling', gb: 'labelling' },
  { us: 'traveled', gb: 'travelled' },
  { us: 'traveling', gb: 'travelling' },
  { us: 'traveler', gb: 'traveller' },
  { us: 'travelers', gb: 'travellers' },
  { us: 'enrollment', gb: 'enrolment' },
  { us: 'fulfill', gb: 'fulfil' },
  { us: 'fulfills', gb: 'fulfils' },
  { us: 'skillful', gb: 'skilful' },

  // -og / -ogue
  { us: 'catalog', gb: 'catalogue' },
  { us: 'catalogs', gb: 'catalogues' },

  // -k / -que (sceptic family)
  { us: 'skeptic', gb: 'sceptic' },
  { us: 'skeptics', gb: 'sceptics' },
  { us: 'skeptical', gb: 'sceptical' },
  { us: 'skepticism', gb: 'scepticism' },

  // -am / -amme
  { us: 'program', gb: 'programme' },
  { us: 'programs', gb: 'programmes' },

  // in- / en- (inquiry family)
  { us: 'inquiry', gb: 'enquiry' },
  { us: 'inquiries', gb: 'enquiries' },

  // Miscellaneous
  { us: 'aging', gb: 'ageing' },
  { us: 'mom', gb: 'mum' },
  { us: 'moms', gb: 'mums' },
  { us: 'gray', gb: 'grey' },
]

/**
 * Words that should never be transformed even if they match a spelling pair.
 * Checked as whole-word, case-insensitive.
 */
const PROTECTED_TERMS = new Set([
  'programming',
  'programmer',
  'programmers',
  'checkbox',
  'checkboxes',
  'check-in',
])

const preserveCase = (source: string, replacement: string) => {
  if (source.toUpperCase() === source) return replacement.toUpperCase()
  if (source[0] === source[0].toUpperCase()) {
    return replacement.charAt(0).toUpperCase() + replacement.slice(1)
  }
  return replacement
}

const replaceWord = (text: string, from: string, to: string) => {
  const pattern = new RegExp(`\\b${from}\\b`, 'gi')
  return text.replace(pattern, (match) => {
    if (PROTECTED_TERMS.has(match.toLowerCase())) return match
    return preserveCase(match, to)
  })
}

/**
 * Convert copy between US and GB English based on market.
 * Source text is assumed to be US English.
 *
 * GB & AU → British spelling (usToGb)
 * US & CA → American spelling (gbToUs, effectively a no-op on US source)
 */
export function localizeCopy(text: string, market: Market): string {
  if (!text) return text

  const useGb = market === 'GB' || market === 'AU'
  const useUs = market === 'US' || market === 'CA'

  if (!useGb && !useUs) return text

  return SPELLING_PAIRS.reduce((acc, pair) => {
    if (useUs) return replaceWord(acc, pair.gb, pair.us)
    return replaceWord(acc, pair.us, pair.gb)
  }, text)
}





